import { useEffect } from "react";
import { Trophy, Medal, Award } from "lucide-react";
import { updatePageSEO } from "@/lib/seo";

const About = () => {
  useEffect(() => {
    updatePageSEO({
      title: "About Diane Corso - Award-Winning Horror Author | Broken Things",
      description: "Learn about Diane Corso, San Francisco horror author and creator of Broken Things. Winner of the 2023 Page Turner Fiction Award and Pacific Northwest Writers' Association honors.",
      keywords: "Diane Corso Author, Diane Corso, about Diane Corso, horror author biography, Broken Things author, award-winning horror writer",
      ogImage: "https://www.dianecorsofiction.com/images/diane-corso-headshot.webp",
      canonical: "https://www.dianecorsofiction.com/about"
    });
  }, []);

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 bg-gothic-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-cinzel font-bold text-blood-red mb-6">About Diane Corso</h1>
            <div className="w-24 h-1 bg-blood-red mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="prose prose-lg max-w-none text-gray-300">
                <p className="mb-6 leading-relaxed">
                  Diane Corso is a San Francisco–based author of supernatural horror and psychological thrillers that blur the line between nightmare and reality. Her stories explore the quiet terrors of grief, guilt, and the unseen worlds that dwell just beneath our own.
                </p>
                <p className="mb-6 leading-relaxed">
                  Her debut thriller, <em>Skin and Bones</em>, was released in 2009 by Bennett & Hastings Publishing. Her latest novel, <em>Broken Things</em> (Muse Literary, 2025), dives deep into haunted spaces—both literal and emotional—through her protagonist, Maggie Oliver, whose search for truth opens a doorway into the supernatural.
                </p>
                <p className="mb-6 leading-relaxed">
                  Winner of the 2023 Page Turner Fiction Award and the Best Paranormal/Supernatural Genre Award, Corso's work has been recognized for its haunting prose and emotional depth.
                </p>
                <p className="mb-6 leading-relaxed">
                  A native Californian and longtime resident of San Francisco, Diane and her husband Michael still live and write in the same cozy apartment they've called home for nearly twenty-five years — proof that some hauntings are welcome.
                </p>
                <p className="mb-8 leading-relaxed">
                  When she's not writing, she's usually researching forgotten urban myths, walking dogs along City shorelines, or finding beauty in hidden places.
                </p>
                <p className="mb-8 leading-relaxed">
                  Join her readers' mailing list for behind-the-scenes notes, early chapters, and news on upcoming releases.
                </p>
                <h3 className="text-2xl font-playfair font-bold text-gothic-white mb-4 mt-8">Awards and Accolades:</h3>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li>Winner, 2023 Page Turner Fiction Award</li>
                  <li>Winner, 2023 Page Turner Best Paranormal/Supernatural Genre</li>
                  <li>Second Place, 2023 PNWA (Pacific Northwest Writers' Association) Horror/Paranormal Genre</li>
                  <li>Finalist, 2023 CIBA Awards, Paranormal Genre</li>
                </ul>
                <blockquote className="text-lg leading-relaxed italic border-l-4 border-blood-red pl-6 my-8 text-gray-300">
                  "This book goes beyond the horror genre and the typical ghost story. It digs deep into that monster: grief, the daily-ness of loss, and how our identity reshapes itself as we heal. Through her instantly likeable protagonist, Corso shows us how to make peace with one's past and give voice to those who've had theirs stolen. I was gripped by this story and turned pages late into the night, losing myself in this mystery as it unfolded in Maggie's lakeside cabin."
                  <footer className="text-sm text-gray-400 mt-4">
                    —Laura Munson, New York Times best-seller, author of The Wild Why, and founder of Haven Writing Retreats.
                  </footer>
                </blockquote>
              </div>
            </div>

            <div className="space-y-8">
              {/* Creative author photo */}
              <div className="text-center">
                <img 
                  src="/images/diane-corso-headshot.webp" 
                  alt="Diane Corso in atmospheric creative setting" 
                  className="w-full rounded-xl shadow-2xl" 
                  data-testid="about-author-photo"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="1000"
                />
              </div>

              {/* Awards Detail */}
              <div className="bg-gothic-charcoal p-6 rounded-xl">
                <h3 className="text-2xl font-playfair font-bold mb-4 text-blood-red">Recognition</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <Trophy className="text-yellow-400 w-5 h-5 mt-1" />
                    <div>
                      <p className="font-semibold">Page Turner Fiction Award</p>
                      <p className="text-sm text-gray-400">2023 Winner</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Medal className="text-yellow-400 w-5 h-5 mt-1" />
                    <div>
                      <p className="font-semibold">Page Turner Best Paranormal/Supernatural Genre</p>
                      <p className="text-sm text-gray-400">2023 Winner</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Award className="text-yellow-400 w-5 h-5 mt-1" />
                    <div>
                      <p className="font-semibold">PNWA Horror/Paranormal Genre</p>
                      <p className="text-sm text-gray-400">2023 Second Place</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Award className="text-yellow-400 w-5 h-5 mt-1" />
                    <div>
                      <p className="font-semibold">CIBA Awards, Paranormal Genre</p>
                      <p className="text-sm text-gray-400">2023 Finalist</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;